import { useEffect, useState } from 'react';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useBulkLicenseActions } from '../../../../hooks/useBulkLicenseActions';
import { licensesManageService } from '../../../../api/licensesManageService';
import type { ManageLicenseListDto, BulkLicenseActionResultDto } from '../../../../types/manageLicenses';

type PresetOption = '+30d' | '+60d' | '+90d' | '+1y' | 'perpetual';

interface Props {
  open: boolean;
  licenses: ManageLicenseListDto[];
  onClose: () => void;
  onCompleted: (result: BulkLicenseActionResultDto | ManageLicenseListDto) => void;
}

export default function LicenseExtendDialog({ open, licenses, onClose, onCompleted }: Props) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<PresetOption | null>(null);
  const [custom, setCustom] = useState<string>('');
  const [reason, setReason] = useState('');
  const { extend: bulkExtendMut } = useBulkLicenseActions();

  useEffect(() => {
    if (!open) return;
    setPreset(null);
    setCustom('');
    setReason('');
  }, [open]);

  if (!open) return null;

  const isBulk = licenses.length > 1;
  const today = new Date().toISOString().split('T')[0];
  const customIsInPast = custom.length > 0 && custom < today;
  const hasSelection = preset !== null || (custom.length > 0 && !customIsInPast);
  const canSubmit = hasSelection && !bulkExtendMut.isPending;

  const handlePresetClick = (p: PresetOption) => {
    setPreset(p);
    setCustom('');
  };

  const handleCustomChange = (val: string) => {
    setCustom(val);
    setPreset(null);
  };

  const submit = async () => {
    const trimReason = reason.trim() || undefined;

    if (preset === 'perpetual') {
      if (isBulk) {
        const result = await bulkExtendMut.mutateAsync({
          licenseIds: licenses.map((l) => l.id),
          newExpirationDate: undefined,
          reason: trimReason,
        });
        onCompleted(result);
      } else {
        const r = await licensesManageService.extend(licenses[0].id, {
          newExpirationDate: null,
          reason: trimReason,
        });
        onCompleted(r);
      }
    } else if (preset) {
      const days = preset === '+1y' ? 365 : Number(preset.replace('+', '').replace('d', ''));
      if (isBulk) {
        const result = await bulkExtendMut.mutateAsync({
          licenseIds: licenses.map((l) => l.id),
          offsetDays: days,
          reason: trimReason,
        });
        onCompleted(result);
      } else {
        const basisIso = licenses[0].expirationDate ?? new Date().toISOString();
        const basis = new Date(basisIso);
        basis.setDate(basis.getDate() + days);
        const r = await licensesManageService.extend(licenses[0].id, {
          newExpirationDate: basis.toISOString(),
          reason: trimReason,
        });
        onCompleted(r);
      }
    } else {
      const iso = new Date(`${custom}T23:59:59Z`).toISOString();
      if (isBulk) {
        const result = await bulkExtendMut.mutateAsync({
          licenseIds: licenses.map((l) => l.id),
          newExpirationDate: iso,
          reason: trimReason,
        });
        onCompleted(result);
      } else {
        const r = await licensesManageService.extend(licenses[0].id, {
          newExpirationDate: iso,
          reason: trimReason,
        });
        onCompleted(r);
      }
    }

    onClose();
  };

  const presets: { label: string; value: PresetOption }[] = [
    { label: '+30d', value: '+30d' },
    { label: '+60d', value: '+60d' },
    { label: '+90d', value: '+90d' },
    { label: '+1y', value: '+1y' },
    { label: t('App.Admin.Licenses.LicenseExtendDialog.PresetPerpetual'), value: 'perpetual' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2 text-neutral-800">
          {isBulk ? t('App.Admin.Licenses.LicenseExtendDialog.TitleBulk', { count: licenses.length }) : t('App.Admin.Licenses.LicenseExtendDialog.TitleSingle')}
        </h2>
        <p className="text-sm text-neutral-700 mb-4">
          {t('App.Admin.Licenses.LicenseExtendDialog.Description')}
        </p>

        {/* Preset chips */}
        <div className="mb-4">
          <p className="text-sm font-medium text-neutral-700 mb-2">{t('App.Admin.Licenses.LicenseExtendDialog.QuickPresetsLabel')}</p>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => handlePresetClick(p.value)}
                className={`px-3 py-1 rounded border text-sm font-medium transition-colors ${
                  preset === p.value
                    ? 'bg-neutral-800 text-white border-neutral-800'
                    : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            {t('App.Admin.Licenses.LicenseExtendDialog.PickDateLabel')}
          </label>
          <input
            type="date"
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-400"
            value={custom}
            min={today}
            onChange={(e) => handleCustomChange(e.target.value)}
          />
          {customIsInPast && (
            <p className="text-xs text-red-600 mt-1">{t('App.Admin.Licenses.LicenseExtendDialog.DateInPastError')}</p>
          )}
        </div>

        {/* Optional reason */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            {t('App.Admin.Licenses.LicenseExtendDialog.ReasonLabel')} <span className="text-neutral-400 font-normal">{t('App.Admin.Licenses.LicenseExtendDialog.ReasonOptional')}</span>
          </label>
          <textarea
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-neutral-400"
            rows={2}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('App.Admin.Licenses.LicenseExtendDialog.ReasonPlaceholder')}
          />
          <p className="text-xs text-neutral-500 text-right mt-0.5">{t('App.Admin.Licenses.LicenseExtendDialog.CharCount', { count: reason.length })}</p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseExtendDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-900 disabled:opacity-50"
          >
            {bulkExtendMut.isPending ? t('App.Admin.Licenses.LicenseExtendDialog.Extending') : t('App.Admin.Licenses.LicenseExtendDialog.Extend')}
          </button>
        </div>
      </div>
    </div>
  );
}
